/**
 * Property Enrichment API (via Backend Proxy)
 * 
 * Multi-API property data for Zillow-like experience
 * Zillow photos + Zestimates + price history + tax history + schools + Walk Score
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../api.config";

export const propertyEnrichmentApi = createApi({
  reducerPath: "propertyEnrichmentApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["EnrichedProperty", "EnrichedSearch"],
  endpoints: (build) => ({
    
    // Get fully enriched property data by address
    getEnrichedProperty: build.query({
      query: ({ address }) => ({
        url: `/property-enrichment/enrich?address=${encodeURIComponent(address)}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data || response,
      providesTags: (_result, _error, { address }) => [
        { type: "EnrichedProperty", id: address },
      ],
      // Keep cached for 30 minutes to match backend cache
      keepUnusedDataFor: 1800,
    }),

    // Search properties with photo-rich results
    searchEnrichedProperties: build.query({
      query: ({
        city,
        state,
        zipCode,
        minPrice,
        maxPrice,
        minBeds,
        maxBeds,
        limit = 20,
        offset = 0,
        listingType = 'sale',
      } = {}) => {
        const params = new URLSearchParams();
        if (city) params.append('city', city);
        if (state) params.append('state', state);
        if (zipCode) params.append('zipCode', zipCode);
        if (minPrice) params.append('minPrice', String(minPrice));
        if (maxPrice) params.append('maxPrice', String(maxPrice));
        if (minBeds) params.append('minBeds', String(minBeds));
        if (maxBeds) params.append('maxBeds', String(maxBeds));
        params.append('limit', String(limit));
        params.append('offset', String(offset));
        params.append('listingType', listingType);
        
        return {
          url: `/property-enrichment/search?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data || response,
      providesTags: ["EnrichedSearch"],
      keepUnusedDataFor: 1800,
    }),
  }),
});

export const {
  useGetEnrichedPropertyQuery,
  useLazyGetEnrichedPropertyQuery,
  useSearchEnrichedPropertiesQuery,
  useLazySearchEnrichedPropertiesQuery,
} = propertyEnrichmentApi;
